import * as React from "react";
import { Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SortDropdown, SortOption } from "@/components/sortDropdown";
import { buildContractFamiliesPath } from "@/views/contract-families/navigation";
import { ContractFamilySnapshotSetupModal } from "@/views/drive/modals/contractFamilySnapshotSetupModal";
import { useNavigate } from "react-router";

interface DriveActionBarProps {
  selectedItemsCount: number;
  selectedFileFseIds: string[];
  selectedFolderFseIds: string[];
  currentFolderFseId?: string;
  currentFolderName?: string;
  canUseAdminBulk?: boolean;
  canGroupFamilies?: boolean;
  hasContractFamilies: boolean;
  onDeleteClick: () => void;
  isDeleteLoading: boolean;
  currentSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  isSearchMode?: boolean;
}

export const DriveActionBar: React.FC<DriveActionBarProps> = ({
  selectedItemsCount,
  selectedFileFseIds,
  selectedFolderFseIds,
  currentFolderFseId,
  currentFolderName,
  canUseAdminBulk = false,
  canGroupFamilies = true,
  hasContractFamilies,
  onDeleteClick,
  isDeleteLoading,
  currentSort,
  onSortChange,
  isSearchMode = false,
}) => (
  <div className="mb-4 flex items-center justify-between">
    {selectedItemsCount > 0 ? (
      <div className="flex items-center gap-2">
        <Button variant="destructive" size="sm" onClick={onDeleteClick} disabled={isDeleteLoading}>
          Delete {selectedItemsCount} {selectedItemsCount === 1 ? "item" : "items"}
        </Button>
      </div>
    ) : (
      <div />
    )}
    {hasContractFamilies && canGroupFamilies && currentFolderFseId && canUseAdminBulk && selectedItemsCount > 0 ? (
      <DriveContractFamiliesAction
        contextFolderFseId={currentFolderFseId}
        contextFolderName={currentFolderName}
        selectedFileFseIds={selectedFileFseIds}
        selectedFolderFseIds={selectedFolderFseIds}
      />
    ) : null}
    {!isSearchMode && (
      <SortDropdown
        onSortChange={onSortChange}
        currentSort={currentSort}
        fields={[
          { key: "name", label: "Name" },
          { key: "created_at", label: "Created Date" },
        ]}
      />
    )}
  </div>
);

interface DriveContractFamiliesActionProps {
  contextFolderFseId: string;
  contextFolderName?: string;
  selectedFileFseIds: string[];
  selectedFolderFseIds: string[];
}

// Administrator bulk path: a bounded selection goes through the reviewed multi-stage pipeline.
// Supplier folders use the one-click header action instead (see DriveHeaderActions).
const DriveContractFamiliesAction: React.FC<DriveContractFamiliesActionProps> = ({
  contextFolderFseId,
  contextFolderName,
  selectedFileFseIds,
  selectedFolderFseIds,
}) => {
  const [isGroupingModalOpen, setIsGroupingModalOpen] = React.useState(false);
  const navigate = useNavigate();
  const handleCreated = React.useCallback(
    (runId: string) => navigate(buildContractFamiliesPath(contextFolderFseId, { pipelineRunId: runId })),
    [contextFolderFseId, navigate],
  );

  return (
    <>
      <Button variant="outline" size="sm" type="button" onClick={() => setIsGroupingModalOpen(true)}>
        <Network className="mr-2 h-4 w-4" />
        Create Contract Summary
      </Button>
      <ContractFamilySnapshotSetupModal
        open={isGroupingModalOpen}
        onOpenChange={setIsGroupingModalOpen}
        contextFolderName={contextFolderName}
        adminSelection={{ contextFolderFseId, selectedFileFseIds, selectedFolderFseIds }}
        onCreated={handleCreated}
      />
    </>
  );
};
